/**
 * Unified Profile Data Hook
 * 
 * Provides consistent access to aggregated profile data across the app.
 * This hook wraps ProfileContext and provides additional utilities for
 * working with profile data in meal planning, recommendations, etc.
 */

import { useProfile } from '../context/ProfileContext';
import { useMemo } from 'react';

/**
 * Unified hook for accessing and using profile data
 * 
 * @returns {Object} Profile data and utility functions
 */
export const useProfileData = () => {
  const {
    profile,
    preferences,
    healthProfile,
    aggregatedProfile,
    loading,
    lastUpdated,
    checkAllergy,
    checkDietaryRestriction,
    checkMedicalCondition,
    refreshProfile
  } = useProfile();

  // Create a comprehensive profile summary for API calls
  const profileForAPI = useMemo(() => {
    if (!profile && !preferences) return null;

    return {
      // Pregnancy profile data
      userId: profile?.userId || preferences?.userId,
      lmpDate: profile?.lmpDate,
      eddDate: profile?.eddDate,
      deliveryDate: profile?.deliveryDate,
      trimester: profile?.trimester,
      currentWeek: profile?.currentWeek,
      isBreastfeeding: profile?.isBreastfeeding,
      
      // Dietary preferences and restrictions
      dietaryRestrictions: preferences?.dietaryRestrictions || profile?.dietaryRestrictions || 'NONE',
      allergies: preferences?.allergies || [],
      foodPreferences: preferences?.foodPreferences || [],
      favoriteFoods: preferences?.favoriteFoods,
      dislikedFoods: preferences?.dislikedFoods,
      
      // Health profile
      medicalConditions: preferences?.medicalConditions || profile?.preexistingConditions,
      complications: preferences?.complications,
      currentWeight: preferences?.currentWeight,
      height: preferences?.height,
      targetWeight: preferences?.targetWeight,
      bmi: healthProfile?.bmi,
      bmiCategory: healthProfile?.bmiCategory,
      
      // Lifestyle preferences
      activityLevel: preferences?.activityLevel || profile?.activityLevel,
      cookingTime: preferences?.cookingTime,
      cookingSkill: preferences?.cookingSkill,
      
      // Pregnancy experience
      morningSicknessSeverity: preferences?.morningSicknessSeverity,
      energyLevel: preferences?.energyLevel,
      hasNausea: profile?.hasNausea || preferences?.morningSicknessSeverity !== 'NONE',
      hasAppetiteIssues: profile?.hasAppetiteIssues,
      
      // Nutritional preferences
      prefersVegetarian: preferences?.prefersVegetarian,
      prefersLowCarb: preferences?.prefersLowCarb,
      prefersHighProtein: preferences?.prefersHighProtein,
      prefersHighFiber: preferences?.prefersHighFiber,
      dailyCalorieGoal: preferences?.dailyCalorieGoal
    };
  }, [profile, preferences, healthProfile]);

  // Check if meal is safe for user (allergies, dietary restrictions, medical conditions)
  const isMealSafe = useMemo(() => {
    return (meal) => {
      if (!meal) return { safe: false, reasons: ['No meal data provided'] };

      const reasons = [];
      let safe = true;

      // Check allergies
      const allergyCheck = checkAllergy(meal.name || '', meal.ingredients || []);
      if (allergyCheck.hasAllergy) {
        safe = false;
        reasons.push(`Contains allergens: ${allergyCheck.allergens.join(', ')}`);
      }

      // Check dietary restrictions
      const restrictionCheck = checkDietaryRestriction(meal);
      if (restrictionCheck.violates) {
        safe = false;
        reasons.push(`Violates dietary restriction: ${restrictionCheck.reason}`);
      }

      // Check medical conditions
      const medicalCheck = checkMedicalCondition(meal);
      if (medicalCheck.requiresAttention && medicalCheck.warnings.length > 0) {
        // Warnings don't make it unsafe, but should be noted
        reasons.push(`Medical considerations: ${medicalCheck.warnings.join('; ')}`);
      }

      return { safe, reasons };
    };
  }, [checkAllergy, checkDietaryRestriction, checkMedicalCondition]);

  // Get active profile factors for display
  const activeFactors = useMemo(() => {
    const factors = [];

    if (aggregatedProfile.allergies?.length > 0) {
      factors.push({
        type: 'allergies',
        label: `${aggregatedProfile.allergies.length} Allergen${aggregatedProfile.allergies.length > 1 ? 's' : ''}`,
        value: aggregatedProfile.allergies.join(', '),
        icon: '🚫',
        color: '#dc3545'
      });
    }

    if (aggregatedProfile.dietaryRestrictions && aggregatedProfile.dietaryRestrictions !== 'NONE') {
      factors.push({
        type: 'dietary',
        label: 'Dietary Restriction',
        value: aggregatedProfile.dietaryRestrictions,
        icon: '🌱',
        color: '#28a745'
      });
    }

    if (aggregatedProfile.medicalConditions) {
      factors.push({
        type: 'medical',
        label: 'Medical Conditions',
        value: aggregatedProfile.medicalConditions,
        icon: '⚕️',
        color: '#ffc107'
      });
    }

    if (aggregatedProfile.morningSicknessSeverity && aggregatedProfile.morningSicknessSeverity !== 'NONE') {
      factors.push({
        type: 'symptoms',
        label: 'Morning Sickness',
        value: aggregatedProfile.morningSicknessSeverity,
        icon: '🤢',
        color: '#ff9800'
      });
    }

    if (aggregatedProfile.energyLevel) {
      factors.push({
        type: 'lifestyle',
        label: 'Energy Level',
        value: aggregatedProfile.energyLevel,
        icon: '⚡',
        color: '#fd7e14'
      });
    }

    if (aggregatedProfile.cookingTime) {
      const timeLabel = aggregatedProfile.cookingTime === 'QUICK' ? '15min' : aggregatedProfile.cookingTime === 'MODERATE' ? '30min' : '60min+';
      factors.push({
        type: 'preference',
        label: `Quick ${timeLabel} recipes`,
        value: aggregatedProfile.cookingTime,
        icon: '⏱️',
        color: '#17a2b8'
      });
    }

    if (aggregatedProfile.cookingSkill) {
      const skillLabel = aggregatedProfile.cookingSkill === 'BEGINNER' ? 'Simple' : aggregatedProfile.cookingSkill === 'INTERMEDIATE' ? 'Moderate' : 'Advanced';
      factors.push({
        type: 'preference',
        label: `${skillLabel} recipes`,
        value: aggregatedProfile.cookingSkill,
        icon: '👩‍🍳',
        color: '#6f42c1'
      });
    }

    if (aggregatedProfile.trimester) {
      factors.push({
        type: 'pregnancy',
        label: `Trimester ${aggregatedProfile.trimester}`,
        value: aggregatedProfile.currentWeek ? `Week ${aggregatedProfile.currentWeek}` : '',
        icon: '👶',
        color: '#e91e63'
      });
    }

    if (aggregatedProfile.activityLevel) {
      factors.push({
        type: 'lifestyle',
        label: 'Activity Level',
        value: aggregatedProfile.activityLevel,
        icon: '🏃',
        color: '#9c27b0'
      });
    }

    return factors;
  }, [aggregatedProfile]);

  // Get filter parameters for meal exploration
  const mealFilters = useMemo(() => {
    return {
      allergies: aggregatedProfile.allergies || [],
      dietaryRestrictions: aggregatedProfile.dietaryRestrictions,
      medicalConditions: aggregatedProfile.medicalConditions,
      morningSickness: aggregatedProfile.morningSicknessSeverity,
      energyLevel: aggregatedProfile.energyLevel,
      cookingTime: aggregatedProfile.cookingTime,
      cookingSkill: aggregatedProfile.cookingSkill,
      trimester: aggregatedProfile.trimester,
      activityLevel: aggregatedProfile.activityLevel,
      prefersVegetarian: preferences?.prefersVegetarian,
      prefersLowCarb: preferences?.prefersLowCarb,
      prefersHighProtein: preferences?.prefersHighProtein,
      prefersHighFiber: preferences?.prefersHighFiber
    };
  }, [aggregatedProfile, preferences]);

  return {
    // Raw data
    profile,
    preferences,
    healthProfile,
    aggregatedProfile,
    
    // Processed data for API
    profileForAPI,
    
    // Utility functions
    checkAllergy,
    checkDietaryRestriction,
    checkMedicalCondition,
    isMealSafe,
    
    // Display data
    activeFactors,
    mealFilters,
    
    // State
    loading,
    lastUpdated,
    refreshProfile,
    
    // Convenience getters
    hasProfileData: !!(profile || preferences),
    hasCompleteProfile: !!(profile && preferences),
    isPregnant: !!(profile?.lmpDate && !profile?.deliveryDate),
    isPostpartum: !!profile?.deliveryDate,
    currentTrimester: aggregatedProfile.trimester,
    currentWeek: aggregatedProfile.currentWeek
  };
};

export default useProfileData;

